import React, { useEffect, useState } from "react";
import { Box, makeStyles, colors } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { FormikHelpers, useFormik } from "formik";
import * as Yup from "yup";

import { InputOtp } from "../../../components/input-otp/InputOtp";
import { InputButton } from "../../../components/input-button/InputButton";
import { CheckOtpRequestAttributes } from "../../../attributes/user-attributes";
import { UserService } from "../../../services/UserService";
import { formatPhoneNumber } from "../../../common/Hooks";
import { Overlay } from "../../../components/overlay/Overlay";
import { SignInStateAttributes } from "../SignInScreen";
import { FormRequestState } from "./CheckPhoneNumber";
import ConnectionLog from "../../../models/ConnectionLog";

interface CheckOtpProps {
  data: SignInStateAttributes;
  otpLength: number;
  callback: (redirect: boolean, log: ConnectionLog) => void;
}

export const useStyles = makeStyles(
  (theme) => ({
    flatButton: {
      display: "inline-block",
      fontSize: 14,
      fontWeight: 500,
      marginRight: "20px",

      cursor: "pointer",
      color: colors.blue[400],
    },

    placeholder: {
      display: "inline-block",
      fontSize: 14,
      fontWeight: 500,
      marginRight: "20px",

      color: colors.grey[800],
    },
  }),
  {
    name: "check-otp-style",
    flip: null,
  }
);

export const CheckOtp = (props: CheckOtpProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  // Props
  const { data, otpLength } = props;
  // State
  const [seconds, setSeconds] = useState(10);

  const formik = useFormik({
    initialValues: {
      telephone: data.phone,
      otp: "",
    },
    validationSchema: Yup.object().shape({
      otp: Yup.string()
        .max(otpLength, intl.formatMessage({ id: "error.invalidOtpCode" }))
        .required(intl.formatMessage({ id: "error.invalidOtpCode" })),
    }),
    onSubmit: async (
      values: CheckOtpRequestAttributes,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        resendOtpformik.setErrors({});
        const response = await UserService.checkOtp(values);
        setSubmitting(false);

        if (response.isSuccess) {
          formik.resetForm();

          switch (response.data.code) {
            case 150: // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            case 103: // Expire code
              setErrors({ unknown: response.data.message });
              break;
            case 105: // Already use
              setErrors({ unknown: response.data.message });
              break;
            case 104: // Invalid
              setErrors({ unknown: response.data.message });
              break;
            case 106: // Success with existing user account
              props.callback(
                true,
                ConnectionLog.fromJson(
                  JSON.parse(JSON.stringify(response.data.data))
                )
              );
              break;
            case 100: // Ok
              // Call main screen callback
              props.callback(
                false,
                ConnectionLog.fromJson(
                  JSON.parse(JSON.stringify(response.data.data))
                )
              );
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (err) {
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  const resendOtpformik = useFormik({
    initialValues: {
      telephone: data.phone,
      country_iso_code: data.countryIsoCode,
    },
    onSubmit: async (
      values: FormRequestState,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        formik.setErrors({});
        const response = await UserService.sendOtp({
          telephone: values.telephone,
          country_iso_code: values.country_iso_code,
        });
        setSubmitting(false);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 105: // Number of issue exceeded
              setErrors({ unknown: response.data.message });
              break;
            case 100: // Ok
              setSeconds(10);
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (err) {
        setSubmitting(false);
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  const handleCodeChange = (otp: string) => {
    formik.setValues({ ...formik.values, otp: otp }, false);
  };

  const otpError = () => {
    if (resendOtpformik.errors.unknown) {
      return resendOtpformik.errors.unknown as string;
    }

    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors.otp) {
      return formik.errors.otp as string;
    }

    if (formik.errors.telephone) {
      return formik.errors.telephone as string;
    }

    return "";
  };

  const renderResendLink = () => {
    return seconds === 0 ? (
      <span
        className={classes.flatButton}
        onClick={(_) => resendOtpformik.handleSubmit()}
      >
        <FormattedMessage id="sharedScreen.resendCode" />
      </span>
    ) : (
      <span className={classes.placeholder}>
        {intl.formatMessage(
          { id: "sharedScreen.resendCodeIn" },
          { timer: seconds }
        )}
      </span>
    );
  };

  const renderError = () => {
    return formik.errors || resendOtpformik.errors ? (
      <Box mt={2} component="div" className="errorWrapper" textAlign="center">
        {otpError()}
      </Box>
    ) : (
      <></>
    );
  };

  useEffect(() => {
    let interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        clearInterval(interval);
      }
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  });

  return (
    <>
      <Box
        marginBottom={2}
        component="span"
        className="title"
        textAlign="center"
      >
        <FormattedMessage id="sharedScreen.verificationCode" />
      </Box>
      <Box component="span" className="subTitle">
        <FormattedMessage id="sharedScreen.typeVerificationCode" />
      </Box>
      <Box component="span" className="subTitle">
        {`${formatPhoneNumber(data.phone)}`}
      </Box>

      <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
        <Box mt={4} component="div">
          <InputOtp
            length={otpLength}
            otp={formik.values.otp}
            onOtpChange={handleCodeChange}
          />
          {renderError()}
          <Box
            mt={5}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="nowrap"
          >
            {renderResendLink()}
            <Box width={100}>
              <InputButton
                color="secondary"
                fullWidth
                size="medium"
                variant="contained"
                type="submit"
                disabled={formik.values.otp.length !== otpLength}
              >
                <FormattedMessage id="sharedScreen.next" />
              </InputButton>
            </Box>
          </Box>
        </Box>
      </form>

      <Overlay loading={formik.isSubmitting || resendOtpformik.isSubmitting} />
    </>
  );
};
